import type { InstancedMesh } from 'three/webgpu';
import { Rng } from '@src/core/Rng';
import { ParticleKind, PARTICLE_STRIDE, type FxRequests } from '@src/ecs/world';
import { ShapeKind, SpritePool } from '@src/graphics/SpritePool';

/**
 * CPU-simulated particles drawn through two procedural SpritePools (additive for
 * sparks and fire, normal blend for goo, rubble and dust). All state lives in
 * typed arrays; emitters are drained from the sim's fx ring each frame.
 */
export class ParticlePool {
  readonly additiveMesh: InstancedMesh;
  readonly solidMesh: InstancedMesh;
  private readonly additive: SpritePool;
  private readonly solid: SpritePool;
  private readonly cap: number;
  private count = 0;
  private readonly x: Float32Array;
  private readonly y: Float32Array;
  private readonly vx: Float32Array;
  private readonly vy: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly size: Float32Array;
  private readonly drag: Float32Array;
  private readonly tint: Uint32Array;
  private readonly blend: Uint8Array;
  /** 0 fade out, 1 shrink out, 2 grow then fade (smoke). */
  private readonly mode: Uint8Array;
  private readonly grav: Float32Array;
  private readonly rng = new Rng(0xc0ffee);

  constructor(capacity: number) {
    this.cap = capacity;
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.maxLife = new Float32Array(capacity);
    this.size = new Float32Array(capacity);
    this.drag = new Float32Array(capacity);
    this.tint = new Uint32Array(capacity);
    this.blend = new Uint8Array(capacity);
    this.mode = new Uint8Array(capacity);
    this.grav = new Float32Array(capacity);
    this.additive = new SpritePool({ capacity, additive: true, renderOrder: 12, shape: ShapeKind.Circle, frames: null, atlas: null });
    this.solid = new SpritePool({ capacity, additive: false, renderOrder: 8, shape: ShapeKind.Circle, frames: null, atlas: null });
    this.additiveMesh = this.additive.mesh;
    this.solidMesh = this.solid.mesh;
  }

  clear(): void {
    this.count = 0;
  }

  private spawn(x: number, y: number, vx: number, vy: number, life: number, size: number, drag: number, tint: number, blend: number, mode: number, grav = 0): void {
    if (this.count >= this.cap) return;
    const i = this.count++;
    this.x[i] = x;
    this.y[i] = y;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.life[i] = life;
    this.maxLife[i] = life;
    this.size[i] = size;
    this.drag[i] = drag;
    this.tint[i] = tint;
    this.blend[i] = blend;
    this.mode[i] = mode;
    this.grav[i] = grav;
  }

  /** Consume emitter requests from the sim. */
  drain(fx: FxRequests): void {
    for (let i = 0; i < fx.particleCount; i++) {
      const o = i * PARTICLE_STRIDE;
      this.emit(fx.particles[o] as ParticleKind, fx.particles[o + 1], fx.particles[o + 2], fx.particles[o + 3], fx.particles[o + 4], fx.particles[o + 5], fx.particles[o + 6]);
    }
    fx.particleCount = 0;
  }

  private emit(kind: ParticleKind, x: number, y: number, dirX: number, dirY: number, power: number, tint: number): void {
    const r = this.rng;
    const len = Math.hypot(dirX, dirY);
    const nx = len > 0.001 ? dirX / len : 0;
    const ny = len > 0.001 ? dirY / len : 0;
    switch (kind) {
      case ParticleKind.MuzzleSparks: {
        const c = tint || 0x9af6ff;
        const n = 4 + Math.round(power * 4);
        for (let i = 0; i < n; i++) {
          const spread = r.range(-0.5, 0.5);
          const cos = Math.cos(spread);
          const sin = Math.sin(spread);
          const sp = r.range(5, 11) * (0.6 + power * 0.6);
          this.spawn(x, y, (nx * cos - ny * sin) * sp, (nx * sin + ny * cos) * sp, r.range(0.08, 0.18), r.range(0.08, 0.16), 6, c, 1, 1);
        }
        break;
      }
      case ParticleKind.BoltTrail: {
        const c = tint || 0x9af6ff;
        this.spawn(x + r.range(-0.08, 0.08), y + r.range(-0.08, 0.08), -nx * r.range(0.5, 1.5), -ny * r.range(0.5, 1.5), r.range(0.12, 0.25), r.range(0.1, 0.2) * (0.7 + power * 0.6), 2, c, 1, 1);
        break;
      }
      case ParticleKind.HitSpark: {
        const c = tint || 0xfff1b0;
        const n = 3 + Math.round(power * 6);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(2, 7) * (0.5 + power);
          this.spawn(x, y, Math.cos(ang) * sp + nx * 2, Math.sin(ang) * sp + ny * 2, r.range(0.1, 0.28), r.range(0.06, 0.14), 5, c, 1, 1);
        }
        break;
      }
      case ParticleKind.ImpGoo: {
        const c = tint || 0xff5ad0;
        const n = 8 + Math.round(power * 6);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(1.5, 6);
          this.spawn(x, y, Math.cos(ang) * sp + nx * 1.5, Math.sin(ang) * sp + ny * 1.5, r.range(0.25, 0.6), r.range(0.1, 0.24), 4, c, 0, 1);
        }
        for (let i = 0; i < 3; i++) this.spawn(x, y, r.range(-1, 1), r.range(-1, 1), r.range(0.2, 0.4), r.range(0.2, 0.35), 3, 0xffa8ea, 1, 0);
        for (let i = 0; i < 4; i++) {
          this.spawn(x, y, r.range(-2.2, 2.2) + nx * 1.2, r.range(1.2, 5) + ny * 0.8, r.range(0.28, 0.55), r.range(0.07, 0.15), 3, c, 0, 1, r.range(16, 24));
        }
        break;
      }
      case ParticleKind.GolemRubble: {
        // power 1 is the death burst; hits use a fraction and chips fly along the blow.
        const c = tint || 0x8e9aa8;
        const chips = Math.round(18 * power);
        for (let i = 0; i < chips; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(1, 7);
          this.spawn(x, y, Math.cos(ang) * sp + nx * 3 * (1 - power), Math.sin(ang) * sp + ny * 3 * (1 - power), r.range(0.4, 1.1), r.range(0.12, 0.34) * (0.6 + power * 0.4), 3, c, 0, 1);
        }
        const motes = Math.round(10 * power);
        for (let i = 0; i < motes; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(2, 9);
          this.spawn(x, y, Math.cos(ang) * sp + nx * 2, Math.sin(ang) * sp + ny * 2, r.range(0.2, 0.5), r.range(0.08, 0.16), 4, 0xc79bff, 1, 1);
        }
        break;
      }
      case ParticleKind.PortalMotes: {
        // Motes drift inward toward the portal center.
        const n = 2 + Math.round(power * 3);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const d = r.range(1.6, 2.6);
          const px = x + Math.cos(ang) * d;
          const py = y + Math.sin(ang) * d;
          const sp = r.range(2.5, 4) * (0.8 + power);
          this.spawn(px, py, -Math.cos(ang) * sp, -Math.sin(ang) * sp, d / sp, r.range(0.08, 0.16), 0, 0xd48bff, 1, 0);
        }
        break;
      }
      case ParticleKind.PortalBurst: {
        const n = Math.round(14 * power);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(3, 10) * (0.5 + power * 0.4);
          this.spawn(x, y, Math.cos(ang) * sp, Math.sin(ang) * sp, r.range(0.3, 0.8), r.range(0.1, 0.3), 3, i % 3 === 0 ? 0xffffff : 0xc46bff, 1, 1);
        }
        if (power > 1.5) {
          for (let i = 0; i < 10; i++) {
            const ang = r.range(0, Math.PI * 2);
            this.spawn(x, y, Math.cos(ang) * r.range(0.5, 2), Math.sin(ang) * r.range(0.5, 2), r.range(0.8, 1.6), r.range(0.5, 1.1), 1.5, 0x3b1a52, 0, 2);
          }
        }
        break;
      }
      case ParticleKind.PickupSparkle: {
        const c = tint || 0xfff3a8;
        const n = 6 + Math.round(power * 8);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(1, 5);
          this.spawn(x, y, Math.cos(ang) * sp, Math.sin(ang) * sp + 1.5, r.range(0.3, 0.7), r.range(0.08, 0.18), 2.5, c, 1, 1);
        }
        break;
      }
      case ParticleKind.Embers: {
        const n = 2 + Math.round(power * 4);
        for (let i = 0; i < n; i++) {
          const c = tint || (i % 2 ? 0xff8a2a : 0xffd27a);
          this.spawn(x + r.range(-0.4, 0.4), y + r.range(-0.4, 0.4), nx * r.range(1, 3) + r.range(-1.2, 1.2), ny * r.range(1, 3) + r.range(-1.2, 1.2), r.range(0.3, 0.8), r.range(0.06, 0.18), 1.5, c, 1, 1);
        }
        break;
      }
      case ParticleKind.Dust: {
        const n = 2 + Math.round(power * 3);
        const c = tint || 0x6a6478;
        for (let i = 0; i < n; i++) {
          this.spawn(x + r.range(-0.15, 0.15), y + r.range(-0.05, 0.05), nx * r.range(0.4, 1.2) + r.range(-0.6, 0.6), ny * r.range(0.4, 1.2) + r.range(0.2, 0.8), r.range(0.25, 0.5), r.range(0.14, 0.3) * (0.7 + power * 0.5), 3, c, 0, 2);
        }
        break;
      }
      case ParticleKind.GateDust: {
        // Dust shakes loose along the gate span (perpendicular to the direction) and drifts with it.
        const n = Math.round(14 * power);
        for (let i = 0; i < n; i++) {
          const along = r.range(-1.4, 1.4);
          const across = r.range(-0.3, 0.3);
          this.spawn(x - ny * along + nx * across, y + nx * along + ny * across, nx * r.range(0.5, 2) + r.range(-1, 1), ny * r.range(0.5, 2) + r.range(-1, 1), r.range(0.4, 0.9), r.range(0.25, 0.5), 2.5, 0x5b5566, 0, 2);
        }
        break;
      }
      case ParticleKind.Confetti: {
        const colors = [0xff3b7a, 0xffd23a, 0x7cff3a, 0x3ad9ff, 0xd07aff];
        const n = Math.round(16 * power);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(2, 8);
          this.spawn(x, y, Math.cos(ang) * sp, Math.sin(ang) * sp, r.range(0.8, 1.6), r.range(0.1, 0.2), 2, colors[i % colors.length], 0, 0);
        }
        break;
      }
      case ParticleKind.BloodSpray: {
        const c = tint || 0x8a1424;
        const n = 8 + Math.round(power * 8);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(2.4, 8.8) * (0.55 + power * 0.5);
          const dx = Math.cos(ang) * 0.58 + nx * 0.42;
          const dy = Math.sin(ang) * 0.58 + ny * 0.42;
          this.spawn(x, y, dx * sp, dy * sp + r.range(1.2, 4.2), r.range(0.22, 0.52), r.range(0.06, 0.17), 3.1, c, 0, 1, r.range(16, 26));
        }
        const flecks = 3 + Math.round(power * 2);
        for (let i = 0; i < flecks; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(3, 9) * (0.5 + power * 0.4);
          this.spawn(x, y, Math.cos(ang) * sp + nx * 2, Math.sin(ang) * sp + ny * 2 + 2, r.range(0.12, 0.28), r.range(0.04, 0.1), 4, c, 1, 1, 12);
        }
        break;
      }
      case ParticleKind.BoneSpray: {
        const shades = [tint || 0xe4d2b0, 0xc8b48c, 0xf0e6d0];
        const n = 7 + Math.round(power * 7);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(3.2, 10) * (0.55 + power * 0.45);
          this.spawn(x, y, Math.cos(ang) * sp + nx * 2.4, Math.sin(ang) * sp + ny * 2.4 + r.range(1, 3.5), r.range(0.28, 0.62), r.range(0.05, 0.16), 2.6, shades[i % 3], 0, 1, r.range(13, 20));
        }
        break;
      }
      case ParticleKind.AshFall: {
        const c = tint || 0x4a382c;
        const n = 6 + Math.round(power * 6);
        for (let i = 0; i < n; i++) {
          this.spawn(x + r.range(-0.35, 0.35), y + r.range(-0.15, 0.25), r.range(-1.1, 1.1) + nx * 0.6, r.range(1.2, 4.2), r.range(0.4, 0.85), r.range(0.1, 0.24) * (0.7 + power * 0.4), 1.6, i % 2 ? c : 0x2a1c16, 0, 2, r.range(6, 10));
        }
        break;
      }
      case ParticleKind.GibChunk: {
        const c = tint || 0x6a2a28;
        const n = 3 + Math.round(power * 3);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(2.2, 7.2) * (0.55 + power * 0.45);
          this.spawn(x, y, Math.cos(ang) * sp + nx * 2, Math.sin(ang) * sp + ny * 2 + r.range(1.5, 4.5), r.range(0.4, 0.78), r.range(0.14, 0.32), 2.4, c, 0, 1, r.range(16, 22));
        }
        break;
      }
      case ParticleKind.MistTear: {
        const c = tint || 0xb8ccff;
        const n = 7 + Math.round(power * 5);
        for (let i = 0; i < n; i++) {
          const ang = r.range(0, Math.PI * 2);
          const sp = r.range(1.1, 4.2) * (0.6 + power * 0.4);
          this.spawn(x, y, Math.cos(ang) * sp + nx * 0.8, Math.sin(ang) * sp + ny * 0.8 + r.range(0.4, 1.8), r.range(0.35, 0.72), r.range(0.1, 0.26), 1.4, c, 1, 2, r.range(-2, 1));
        }
        break;
      }
      default:
        break;
    }
  }

  update(dt: number): void {
    let n = this.count;
    for (let i = 0; i < n; ) {
      this.life[i] -= dt;
      if (this.life[i] <= 0) {
        // Swap-remove keeps the arrays dense without allocation.
        n--;
        this.x[i] = this.x[n];
        this.y[i] = this.y[n];
        this.vx[i] = this.vx[n];
        this.vy[i] = this.vy[n];
        this.life[i] = this.life[n];
        this.maxLife[i] = this.maxLife[n];
        this.size[i] = this.size[n];
        this.drag[i] = this.drag[n];
        this.tint[i] = this.tint[n];
        this.blend[i] = this.blend[n];
        this.mode[i] = this.mode[n];
        this.grav[i] = this.grav[n];
        continue;
      }
      const k = Math.exp(-this.drag[i] * dt);
      this.vx[i] *= k;
      this.vy[i] = this.vy[i] * k - this.grav[i] * dt;
      this.x[i] += this.vx[i] * dt;
      this.y[i] += this.vy[i] * dt;
      i++;
    }
    this.count = n;

    this.additive.begin();
    this.solid.begin();
    for (let i = 0; i < n; i++) {
      const t = this.life[i] / this.maxLife[i]; // 1 -> 0
      let size = this.size[i];
      let alpha = 1;
      switch (this.mode[i]) {
        case 1:
          size *= 0.3 + 0.7 * t;
          alpha = Math.min(1, t * 3);
          break;
        case 2:
          size *= 1.6 - 0.6 * t;
          alpha = t * 0.7;
          break;
        default:
          alpha = t;
          break;
      }
      const pool = this.blend[i] ? this.additive : this.solid;
      pool.push(this.x[i], this.y[i], 0.75, 0, size, size, this.tint[i], alpha, 0);
    }
    this.additive.end();
    this.solid.end();
  }

  get live(): number {
    return this.count;
  }

  dispose(): void {
    this.additive.dispose();
    this.solid.dispose();
  }
}
